import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { RemixIcon } from "@/components/shared/RemixIcon";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProfileData {
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  phone?: string;
  phoneNumber?: string;
  phone_number?: string;
}

const Profile = () => {
  const { user, userRole } = useAuth();

  // Fetch user profile data from the users table
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('profile, phone_number')
        .eq('auth_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id,
  });

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  // Get user display name from profile or fallback to email
  const getDisplayName = () => {
    const profile = (userProfile?.profile as ProfileData) || {};
    const firstName = profile.firstName || profile.first_name || '';
    const lastName = profile.lastName || profile.last_name || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    if (!user?.email) return "Unknown User";
    // Extract name part from email before @
    const emailName = user.email.split('@')[0];
    return emailName.charAt(0).toUpperCase() + emailName.slice(1);
  };

  // Get phone number from profile
  const getPhoneNumber = () => {
    const profile = (userProfile?.profile as ProfileData) || {};
    return profile.phone || profile.phoneNumber || profile.phone_number || userProfile?.phone_number || 'Not provided';
  };

  const getRoleBadgeVariant = (role: string | null) => {
    switch (role) {
      case 'admin': return 'default';
      case 'tailor': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground">
          Manage your account information and settings
        </p>
      </div>

      {/* Profile Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <CardTitle className="text-2xl">{getDisplayName()}</CardTitle>
              <CardDescription className="text-base">{user?.email}</CardDescription>
              <Badge variant={getRoleBadgeVariant(userRole)} className="w-fit">
                {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'User'}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Account Information */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RemixIcon name="user-line" />
              Account Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Full Name</label>
              <p className="text-sm">{getDisplayName()}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Email Address</label>
              <p className="text-sm">{user?.email || 'Not provided'}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
              <p className="text-sm">{getPhoneNumber()}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RemixIcon name="shield-user-line" />
              Role & Permissions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Current Role</label>
              <div className="flex items-center gap-2">
                <Badge variant={getRoleBadgeVariant(userRole)}>
                  {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'User'}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Permissions</label>
              <div className="text-sm space-y-1">
                {userRole === 'admin' ? (
                  <>
                    <p className="flex items-center gap-2 text-green-600">
                      <RemixIcon name="check-line" size="sm" />
                      Full system access
                    </p>
                    <p className="flex items-center gap-2 text-green-600">
                      <RemixIcon name="check-line" size="sm" />
                      User management
                    </p>
                    <p className="flex items-center gap-2 text-green-600">
                      <RemixIcon name="check-line" size="sm" />
                      Billing & subscriptions
                    </p>
                    <p className="flex items-center gap-2 text-green-600">
                      <RemixIcon name="check-line" size="sm" />
                      Reports & analytics
                    </p>
                  </>
                ) : (
                  <p className="flex items-center gap-2 text-blue-600">
                    <RemixIcon name="check-line" size="sm" />
                    Standard user access
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RemixIcon name="lock-line" />
            Security Information
          </CardTitle>
          <CardDescription>
            Account security and authentication details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Email Verified</label>
              <div className="flex items-center gap-2">
                {user?.email_confirmed_at ? (
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    <RemixIcon name="check-line" size="sm" className="mr-1" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <RemixIcon name="close-line" size="sm" className="mr-1" />
                    Not Verified
                  </Badge>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Last Sign In</label>
              <p className="text-sm">
                {user?.last_sign_in_at 
                  ? new Date(user.last_sign_in_at).toLocaleString()
                  : 'Not available'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
